/**
 * Tracks for drawing only: each track's centre and box size smoothed over time and filled in on every frame, so a
 * dot or box glides with the object instead of stepping every `step` frames (the general search reads every 3rd
 * frame) and wobbling with the detector. Counting keeps the tracks as they are (tracksModel.indexTracks).
 *
 * Each frame's value is a local linear fit of the track's samples around it, weighted by a gaussian of SMOOTH_SEC:
 * a moving object is neither dragged behind nor rounded off at the ends of its track, as an average would. Samples
 * more than MAX_GAP_SEC apart are not bridged (the object was not seen in between), nor a jump of more than
 * JUMP_BODIES body lengths between two samples (a cut, or the tracker hopping): the dot jumps there, it does not
 * slide across the picture.
 */

export const SMOOTH_SEC = 0.2;
export const MAX_GAP_SEC = 0.6;
export const JUMP_BODIES = 1.5;

const KEYS = ['x', 'y', 'w', 'h'];

// local linear fit at frame f of the samples a..b (indices into t / values), gaussian weights of sigma frames
const fitAt = (t, values, a, b, f, sigma) => {
	let s0 = 0;
	let s1 = 0;
	let s2 = 0;
	const v0 = new Float64Array(values.length);
	const v1 = new Float64Array(values.length);
	const reach = 3 * sigma;
	for (let k = a; k <= b; k += 1) {
		const dt = t[k] - f;
		if (dt < -reach) continue;
		if (dt > reach) break;
		const w = Math.exp(-0.5 * (dt / sigma) ** 2);
		s0 += w;
		s1 += w * dt;
		s2 += w * dt * dt;
		for (let q = 0; q < values.length; q += 1) {
			v0[q] += w * values[q][k];
			v1[q] += w * dt * values[q][k];
		}
	}
	const det = s0 * s2 - s1 * s1;
	return values.map((_, q) =>
		det > 1e-9 * s0 * s0 ? (s2 * v0[q] - s1 * v1[q]) / det : v0[q] / s0
	);
};

// whether the track moves more than JUMP_BODIES body lengths from sample k to the next
const jumps = (tr, k) =>
	Math.hypot(tr.x[k + 1] - tr.x[k], tr.y[k + 1] - tr.y[k]) >
	JUMP_BODIES * Math.max(1, Math.sqrt(tr.w[k] * tr.h[k]));

/** One track with its points on every frame between its samples (and `hold` frames after each run), smoothed. */
export const smoothTrack = (track, { fps = 30, step = 1, hold = 1 } = {}) => {
	const t = track.t || [];
	if (!t.length) return track;
	const sigma = Math.max(step, SMOOTH_SEC * fps);
	const maxGap = Math.max(2 * step, Math.round(MAX_GAP_SEC * fps));
	const values = KEYS.map(k => track[k]);
	const out = { t: [], x: [], y: [], w: [], h: [] };
	let a = 0;
	while (a < t.length) {
		let b = a;
		while (b + 1 < t.length && t[b + 1] - t[b] <= maxGap && !jumps(track, b)) b += 1;
		const end = t[b] + Math.max(0, hold - 1);
		for (let f = t[a]; f <= end; f += 1) {
			const v = fitAt(t, values, a, b, Math.min(f, t[b]), sigma);
			out.t.push(f);
			KEYS.forEach((k, q) => out[k].push(k === 'w' || k === 'h' ? Math.max(1, v[q]) : v[q]));
		}
		a = b + 1;
	}
	return { ...track, ...out };
};

/** The document with every track smoothed for drawing; `step` becomes 1 (every frame has its point). */
export const smoothForDrawing = doc => {
	if (!doc?.tracks) return doc;
	const fps = doc.fps || 30;
	const step = Math.max(1, doc.step | 0);
	return {
		...doc,
		step: 1,
		tracks: doc.tracks.map(tr => smoothTrack(tr, { fps, step, hold: step })),
	};
};
